/*
 * Postal Code Validator
 * ---------------------
 * Standalone, reusable client-side postal/ZIP validator for 30 countries.
 * Two public APIs are exposed on window:
 *
 *   1) PostalCodeValidator  — framework-agnostic validator
 *      - PostalCodeValidator.validate(country, code)
 *          returns { valid: boolean, error: string|null, example: string }
 *      - PostalCodeValidator.countries()
 *          returns [{ code, name }, ...] sorted alphabetically by name
 *      - PostalCodeValidator.addPattern(code, { name, regex, example })
 *          extend the country map at runtime
 *      - PostalCodeValidator.patterns
 *          read access to the raw pattern map
 *
 *   2) am.$.fn.postalCodeValidator(options)  — jQuery plugin
 *      Options:
 *          countrySelector — selector for the country <select> (required)
 *          onValidate(result, $input) — optional callback after each validation
 *      Behavior:
 *          - Listens for 'input' and 'blur' on the input, 'change' on the select
 *          - Toggles .is-valid / .is-invalid on the input
 *          - Empty input is neutral (no class, no error)
 *
 * Example:
 *      var r = PostalCodeValidator.validate('US', '90210');
 *      // -> { valid: true, error: null, example: '90210 or 90210-1234' }
 *
 *      am.$('#postal_code').postalCodeValidator({
 *          countrySelector: '#country',
 *          onValidate: function(result, $input) {
 *              $input.siblings('.error').text(result.error || '');
 *          }
 *      });
 */

(function (root) {
    'use strict';

    var patterns = {
        US: { name: 'United States',  regex: /^\d{5}(-\d{4})?$/,                          example: '90210 or 90210-1234' },
        CA: { name: 'Canada',         regex: /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/,     example: 'K1A 0B1' },
        MX: { name: 'Mexico',         regex: /^\d{5}$/,                                    example: '01000' },
        GB: { name: 'United Kingdom', regex: /^[A-Za-z]{1,2}\d[A-Za-z\d]?\s*\d[A-Za-z]{2}$/, example: 'SW1A 1AA' },
        IE: { name: 'Ireland',        regex: /^[A-Za-z]\d{2}\s?[A-Za-z0-9]{4}$/,          example: 'D02 AF30' },
        DE: { name: 'Germany',        regex: /^\d{5}$/,                                    example: '10115' },
        FR: { name: 'France',         regex: /^\d{5}$/,                                    example: '75001' },
        ES: { name: 'Spain',          regex: /^\d{5}$/,                                    example: '28001' },
        IT: { name: 'Italy',          regex: /^\d{5}$/,                                    example: '00100' },
        PT: { name: 'Portugal',       regex: /^\d{4}-\d{3}$/,                              example: '1000-001' },
        NL: { name: 'Netherlands',    regex: /^\d{4}\s?[A-Za-z]{2}$/,                      example: '1012 JS' },
        BE: { name: 'Belgium',        regex: /^\d{4}$/,                                    example: '1000' },
        CH: { name: 'Switzerland',    regex: /^\d{4}$/,                                    example: '8001' },
        AT: { name: 'Austria',        regex: /^\d{4}$/,                                    example: '1010' },
        SE: { name: 'Sweden',         regex: /^\d{3}\s?\d{2}$/,                            example: '114 55' },
        NO: { name: 'Norway',         regex: /^\d{4}$/,                                    example: '0150' },
        DK: { name: 'Denmark',        regex: /^\d{4}$/,                                    example: '1050' },
        FI: { name: 'Finland',        regex: /^\d{5}$/,                                    example: '00100' },
        PL: { name: 'Poland',         regex: /^\d{2}-\d{3}$/,                              example: '00-001' },
        AU: { name: 'Australia',      regex: /^\d{4}$/,                                    example: '2000' },
        NZ: { name: 'New Zealand',    regex: /^\d{4}$/,                                    example: '6011' },
        JP: { name: 'Japan',          regex: /^\d{3}-?\d{4}$/,                             example: '100-0001' },
        CN: { name: 'China',          regex: /^\d{6}$/,                                    example: '100000' },
        IN: { name: 'India',          regex: /^\d{6}$/,                                    example: '110001' },
        KR: { name: 'South Korea',    regex: /^\d{5}$/,                                    example: '03187' },
        SG: { name: 'Singapore',      regex: /^\d{6}$/,                                    example: '238859' },
        BR: { name: 'Brazil',         regex: /^\d{5}-?\d{3}$/,                             example: '01310-100' },
        AR: { name: 'Argentina',      regex: /^([A-Za-z]\d{4}[A-Za-z]{3}|\d{4})$/,         example: 'C1425CLA or 1425' },
        ZA: { name: 'South Africa',   regex: /^\d{4}$/,                                    example: '8001' },
        IL: { name: 'Israel',         regex: /^\d{5}(\d{2})?$/,                            example: '91000 or 9100000' }
    };

    // Country-name → ISO map. Country <select> options in this app store the
    // full country name as the value (e.g. "United States"), not the ISO code.
    var nameToIso = {
        'United States': 'US',
        'Canada':        'CA',
        'Mexico':        'MX',
        'United Kingdom':'GB',
        'Ireland':       'IE',
        'Germany':       'DE',
        'France':        'FR',
        'Spain':         'ES',
        'Italy':         'IT',
        'Portugal':      'PT',
        'Netherlands':   'NL',
        'Belgium':       'BE',
        'Switzerland':   'CH',
        'Austria':       'AT',
        'Sweden':        'SE',
        'Norway':        'NO',
        'Denmark':       'DK',
        'Finland':       'FI',
        'Poland':        'PL',
        'Australia':     'AU',
        'New Zealand':   'NZ',
        'Japan':         'JP',
        'China':         'CN',
        'India':         'IN',
        'South Korea':   'KR',
        'Singapore':     'SG',
        'Brazil':        'BR',
        'Argentina':     'AR',
        'South Africa':  'ZA',
        'Israel':        'IL'
    };

    function getIsoByName(name) {
        if (!name) return undefined;
        return nameToIso[String(name).replace(/^\s+|\s+$/g, '')];
    }

    // Given a zip/postal input element, find the related country <select> by
    // swapping "zip" for "country" in its id. Returns the ISO code or undefined
    // if no related select exists or its value isn't a recognized country name.
    function findCountryIsoFor(zipEle) {
        if (!zipEle || !zipEle.id || zipEle.id.indexOf('zip') === -1) return undefined;
        var countryId = zipEle.id.replace('zip', 'country');
        var countryEle = document.getElementById(countryId);
        if (!countryEle) return undefined;
        return getIsoByName(countryEle.value);
    }

    function validate(country, code) {
        var key = (country || '').toString().toUpperCase();
        var entry = patterns[key];

        if (!entry) {
            return { valid: false, error: 'Unsupported country: ' + country, example: '' };
        }

        var trimmed = (code == null ? '' : String(code)).replace(/^\s+|\s+$/g, '');

        if (trimmed === '') {
            return { valid: false, error: 'Postal code is required', example: entry.example };
        }

        if (!entry.regex.test(trimmed)) {
            return { valid: false, error: 'Invalid format. Expected: ' + entry.example, example: entry.example };
        }

        return { valid: true, error: null, example: entry.example };
    }

    function countries() {
        var list = [];
        for (var code in patterns) {
            if (Object.prototype.hasOwnProperty.call(patterns, code)) {
                list.push({ code: code, name: patterns[code].name });
            }
        }
        list.sort(function (a, b) {
            if (a.name < b.name) return -1;
            if (a.name > b.name) return 1;
            return 0;
        });
        return list;
    }

    function addPattern(code, def) {
        if (!code || !def || !def.regex) return;
        patterns[String(code).toUpperCase()] = {
            name: def.name || code,
            regex: def.regex,
            example: def.example || ''
        };
    }

    root.PostalCodeValidator = {
        validate: validate,
        countries: countries,
        addPattern: addPattern,
        patterns: patterns,
        nameToIso: nameToIso,
        getIsoByName: getIsoByName,
        findCountryIsoFor: findCountryIsoFor
    };

    // jQuery plugin — attached to am.$ since that's this repo's jQuery namespace.
    // Skips silently if am.$ isn't available; the core validator still works.
    var $j = (root.am && root.am.$) ? root.am.$ : null;
    if ($j && $j.fn) {
        $j.fn.postalCodeValidator = function (options) {
            var opts = $j.extend({ countrySelector: null, onValidate: null }, options || {});

            return this.each(function () {
                var $input = $j(this);

                function run() {
                    var raw = $input.val();
                    var val = (raw == null ? '' : String(raw)).replace(/^\s+|\s+$/g, '');
                    var country = opts.countrySelector ? $j(opts.countrySelector).val() : '';

                    if (val === '') {
                        $input.removeClass('is-valid is-invalid');
                        if (typeof opts.onValidate === 'function') {
                            opts.onValidate({ valid: false, error: null, example: '', neutral: true }, $input);
                        }
                        return;
                    }

                    var result = validate(country, raw);
                    $input
                        .toggleClass('is-valid',   result.valid)
                        .toggleClass('is-invalid', !result.valid);

                    if (typeof opts.onValidate === 'function') {
                        opts.onValidate(result, $input);
                    }
                }

                $input.on('input.postalCodeValidator blur.postalCodeValidator', run);

                if (opts.countrySelector) {
                    $j(opts.countrySelector).on('change.postalCodeValidator', run);
                }
            });
        };
    }
})(typeof window !== 'undefined' ? window : this);
